import { useApi } from '@renderer/chat'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer.ts'
import { Insight, InsightWithSource } from '@shared/utils/insights/types'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

export function useInsights() {
  const { get } = useApi()

  return useQuery<Insight[], Error>({
    queryKey: ['insights'],
    queryFn: async () => {
      const resp = await get('insights', { version: 'v1' })
      if (!resp.ok) {
        throw new Error(resp.statusText ?? `Unexpected ${resp.status} error`)
      }
      const json = await resp.json()
      if (!json.success) {
        throw new Error('Unexpected error')
      }
      return json.data as Insight[]
    },
  })
}

export function useTodaysInsights() {
  const { get } = useApi()

  return useQuery<Insight[], Error>({
    queryKey: ['todays-insights'],
    queryFn: async () => {
      const resp = await get('insights', { version: 'v1' })
      if (!resp.ok) {
        throw new Error(resp.statusText ?? `Unexpected ${resp.status} error`)
      }
      const json = (await resp.json()) as { success: boolean; data: Insight[] }
      if (!json.success) {
        throw new Error('Unexpected error')
      }

      const today = new Date()
      const todaysInsights = json.data.filter((insight) => {
        const insightDate = new Date(insight.createdAt)
        return insightDate.toDateString() === today.toDateString()
      })

      const insightsWithSources: InsightWithSource[] = await Promise.all(
        todaysInsights.map(async (insight) => ({
          ...insight,
          meetingNote:
            insight.source === 'audio-note' && insight.sourceId
              ? await AudioNoteIpcRenderer.getAudioNote(insight.sourceId)
              : undefined,
        })),
      )
      return insightsWithSources
    },
  })
}

function groupInsightsBySource(insights: InsightWithSource[]): Record<string, Insight[]> {
  return insights.reduce(
    (groups, insight) => {
      let groupKey: string

      // Build base group key
      if (insight.source === 'audio-note') {
        groupKey = `audio-note:${insight.sourceId}`
      } else if (insight.assignedBy) {
        groupKey = `assignedBy:${insight.assignedBy.toLowerCase()}`
      } else if (insight.screenSource?.url) {
        groupKey = `url:${new URL(insight.screenSource.url).hostname}`
      } else if (insight.screenSource?.appName) {
        groupKey = `app:${insight.screenSource.appName}`
      } else {
        groupKey = 'unknown'
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }

      groups[groupKey].push(insight)
      return groups
    },
    {} as Record<string, Insight[]>,
  )
}

export function useTodaysInsightsBySource() {
  const insights = useTodaysInsights()
  return useMemo(() => {
    if (!insights.data) return { ...insights, data: {} }

    const grouped = groupInsightsBySource(insights.data)

    // 1) Sort items within each group:
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const aCompleted = a.status === 'completed'
        const bCompleted = b.status === 'completed'
        if (aCompleted && !bCompleted) return 1 // completed last
        if (!aCompleted && bCompleted) return -1
        // both completed or both not → most recent first
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      })
    })

    // 2) Reorder groups: groups with all completed go last.
    //    Within each bucket, sort by the group's most recent updatedAt (desc)
    const entries = Object.entries(grouped)
    entries.sort(([, a], [, b]) => {
      const aAllCompleted = a.every((i) => i.status === 'completed')
      const bAllCompleted = b.every((i) => i.status === 'completed')
      if (aAllCompleted !== bAllCompleted) return aAllCompleted ? 1 : -1

      const aMax = Math.max(...a.map((i) => new Date(i.updatedAt).getTime()))
      const bMax = Math.max(...b.map((i) => new Date(i.updatedAt).getTime()))
      return bMax - aMax
    })

    // Preserve insertion order by creating a new object in the sorted order
    const ordered: Record<string, InsightWithSource[]> = {}
    for (const [k, v] of entries) ordered[k] = v

    return {
      ...insights,
      data: ordered,
    }
  }, [insights])
}

export function useTodaysInsightsPeople() {
  const insights = useInsights()

  return useMemo(() => {
    if (!insights.data) return []

    const uniqueAssignedBy = new Set<string>()

    insights.data.forEach((insight) => {
      if (insight.assignedBy) {
        uniqueAssignedBy.add(insight.assignedBy.toLowerCase())
      }
    })

    return Array.from(uniqueAssignedBy).sort((a, b) => a.localeCompare(b))
  }, [insights.data])
}

export function useInsightById(insightId: string | undefined) {
  const { get } = useApi()

  return useQuery<Insight | null, Error>({
    queryKey: ['insight', insightId],
    queryFn: async () => {
      if (!insightId) {
        return null
      }

      const resp = await get(`insights/${insightId}`, { version: 'v1' })

      if (!resp.ok) {
        // If not found, return null instead of throwing
        if (resp.status === 404) {
          return null
        }
        throw new Error(resp.statusText ?? `Unexpected ${resp.status} error`)
      }

      const json = (await resp.json()) as { success: boolean; data: Insight }
      if (!json.success) {
        throw new Error('Unexpected error')
      }

      return json.data
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    enabled: !!insightId,
  })
}
